const PROTECTED_MAGIC = [0x42, 0x58, 0x4f, 0x31]; // BXO1
const PROTECTED_TAIL = [0x42, 0x58, 0x45, 0x31]; // BXE1
const PROTECTED_VERSION = 1;
const HEADER_LENGTH = 20;
const TAIL_LENGTH = 4;

const PROFILE = {
	STATIC_SHIFT_4K: 'STATIC_SHIFT_4K',
	MEDIA_SHIFT_64K: 'MEDIA_SHIFT_64K'
};

const PROFILE_CODE_MAP = {
	[PROFILE.STATIC_SHIFT_4K]: 1,
	[PROFILE.MEDIA_SHIFT_64K]: 2
};

const CODE_PROFILE_MAP = {
	1: PROFILE.STATIC_SHIFT_4K,
	2: PROFILE.MEDIA_SHIFT_64K
};

const EXT_CONTENT_TYPE_MAP = Object.freeze({
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	png: 'image/png',
	webp: 'image/webp',
	gif: 'image/gif',
	bmp: 'image/bmp',
	svg: 'image/svg+xml',
	mp4: 'video/mp4',
	mov: 'video/quicktime',
	webm: 'video/webm',
	ogg: 'audio/ogg',
	mp3: 'audio/mpeg',
	wav: 'audio/wav',
	m4a: 'audio/mp4',
	aac: 'audio/aac',
	amr: 'audio/amr',
	'3gp': 'audio/3gpp',
	'3gpp': 'audio/3gpp',
	pdf: 'application/pdf',
	txt: 'text/plain',
	json: 'application/json',
	csv: 'text/csv',
	doc: 'application/msword',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	xls: 'application/vnd.ms-excel',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	ppt: 'application/vnd.ms-powerpoint',
	pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	zip: 'application/zip',
	rar: 'application/vnd.rar',
	'7z': 'application/x-7z-compressed'
});

function isClientDecryptSupported() {
	return typeof window !== 'undefined'
		&& typeof window.Blob !== 'undefined'
		&& typeof window.URL !== 'undefined'
		&& typeof window.URL.createObjectURL === 'function'
		&& typeof window.Uint8Array !== 'undefined';
}

function toUint8Array(input) {
	if (input instanceof Uint8Array) {
		return input;
	}
	if (input instanceof ArrayBuffer) {
		return new Uint8Array(input);
	}
	if (ArrayBuffer.isView(input)) {
		return new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
	}
	return new Uint8Array(0);
}

function getFileExtension(fileName) {
	const normalized = String(fileName || '');
	const idx = normalized.lastIndexOf('.');
	if (idx < 0) {
		return '';
	}
	return normalized.slice(idx + 1).toLowerCase();
}

function inferProfile({ mimeType, fileName, profile }) {
	if (profile && PROFILE_CODE_MAP[profile]) {
		return profile;
	}
	const lowerType = String(mimeType || '').toLowerCase();
	if (lowerType.startsWith('video/') || lowerType.startsWith('audio/')) {
		return PROFILE.MEDIA_SHIFT_64K;
	}
	const ext = getFileExtension(fileName);
	if (['mp4', 'mov', 'm4v', 'webm', 'ogg', 'mp3', 'wav', 'm4a', 'aac', 'flac', 'amr', '3gp', '3gpp'].includes(ext)) {
		return PROFILE.MEDIA_SHIFT_64K;
	}
	return PROFILE.STATIC_SHIFT_4K;
}

function headShiftByProfile(profile) {
	if (profile === PROFILE.MEDIA_SHIFT_64K) {
		return 64 * 1024;
	}
	return 4 * 1024;
}

function matchesAt(bytes, values, offset) {
	if (offset < 0 || offset + values.length > bytes.length) {
		return false;
	}
	for (let i = 0; i < values.length; i += 1) {
		if (bytes[offset + i] !== values[i]) {
			return false;
		}
	}
	return true;
}

function protectAssetBytes(inputBytes, options = {}) {
	const plainBytes = toUint8Array(inputBytes);
	if (!plainBytes.length) {
		return {
			payloadBytes: plainBytes,
			format: 'RAW',
			version: 0,
			profile: PROFILE.STATIC_SHIFT_4K,
			headShiftBytes: 0,
			originalSize: 0
		};
	}
	const profile = inferProfile(options);
	const headShiftBytes = Math.min(headShiftByProfile(profile), plainBytes.length);
	const tailLength = plainBytes.length - headShiftBytes;
	const payloadLength = HEADER_LENGTH + tailLength + headShiftBytes + TAIL_LENGTH;
	const payloadBytes = new Uint8Array(payloadLength);
	payloadBytes.set(PROTECTED_MAGIC, 0);
	const headerView = new DataView(payloadBytes.buffer, 0, HEADER_LENGTH);
	headerView.setUint8(4, PROTECTED_VERSION);
	headerView.setUint8(5, PROFILE_CODE_MAP[profile] || PROFILE_CODE_MAP[PROFILE.STATIC_SHIFT_4K]);
	headerView.setUint16(6, 0, false);
	headerView.setUint32(8, headShiftBytes, false);
	headerView.setUint32(12, plainBytes.length, false);
	headerView.setUint32(16, 0, false);
	payloadBytes.set(plainBytes.subarray(headShiftBytes), HEADER_LENGTH);
	payloadBytes.set(plainBytes.subarray(0, headShiftBytes), HEADER_LENGTH + tailLength);
	payloadBytes.set(PROTECTED_TAIL, payloadLength - TAIL_LENGTH);
	return {
		payloadBytes,
		format: 'BXO1',
		version: PROTECTED_VERSION,
		profile,
		headShiftBytes,
		originalSize: plainBytes.length
	};
}

function createProtectedBlobFromFile(file, options = {}) {
	if (!(file instanceof Blob)) {
		throw new Error('文件对象无效');
	}
	const profile = inferProfile(options);
	const headShiftBytes = Math.min(headShiftByProfile(profile), file.size);
	const bodyLength = file.size - headShiftBytes;
	const headerBytes = new Uint8Array(HEADER_LENGTH);
	headerBytes.set(PROTECTED_MAGIC, 0);
	const headerView = new DataView(headerBytes.buffer);
	headerView.setUint8(4, PROTECTED_VERSION);
	headerView.setUint8(5, PROFILE_CODE_MAP[profile] || PROFILE_CODE_MAP[PROFILE.STATIC_SHIFT_4K]);
	headerView.setUint16(6, 0, false);
	headerView.setUint32(8, headShiftBytes, false);
	headerView.setUint32(12, file.size, false);
	headerView.setUint32(16, 0, false);
	const payloadBlob = new Blob([
		headerBytes,
		file.slice(headShiftBytes),
		file.slice(0, headShiftBytes),
		new Uint8Array(PROTECTED_TAIL)
	], {
		type: String(options.mimeType || file.type || '').trim() || 'application/octet-stream'
	});
	return {
		payloadBlob,
		format: 'BXO1',
		version: PROTECTED_VERSION,
		profile,
		headShiftBytes,
		originalSize: file.size,
		payloadSize: HEADER_LENGTH + bodyLength + headShiftBytes + TAIL_LENGTH
	};
}

function restoreProtectedAssetBytes(inputBytes) {
	const cipherBytes = toUint8Array(inputBytes);
	if (cipherBytes.length < HEADER_LENGTH + TAIL_LENGTH) {
		return {
			bytes: cipherBytes,
			protected: false,
			format: 'RAW',
			version: 0,
			profile: null
		};
	}
	if (!matchesAt(cipherBytes, PROTECTED_MAGIC, 0)
		|| !matchesAt(cipherBytes, PROTECTED_TAIL, cipherBytes.length - TAIL_LENGTH)) {
		return {
			bytes: cipherBytes,
			protected: false,
			format: 'RAW',
			version: 0,
			profile: null
		};
	}
	const view = new DataView(cipherBytes.buffer, cipherBytes.byteOffset, cipherBytes.byteLength);
	const version = view.getUint8(4);
	if (version !== PROTECTED_VERSION) {
		return {
			bytes: cipherBytes,
			protected: false,
			format: 'RAW',
			version,
			profile: null
		};
	}
	const profile = CODE_PROFILE_MAP[view.getUint8(5)] || PROFILE.STATIC_SHIFT_4K;
	const headShiftBytes = view.getUint32(8, false);
	const originalSize = view.getUint32(12, false);
	if (originalSize <= 0 || originalSize > cipherBytes.length) {
		return {
			bytes: cipherBytes,
			protected: false,
			format: 'RAW',
			version,
			profile
		};
	}
	const tailLength = originalSize - headShiftBytes;
	const expectedLength = HEADER_LENGTH + tailLength + headShiftBytes + TAIL_LENGTH;
	if (headShiftBytes < 0 || tailLength < 0 || expectedLength > cipherBytes.length) {
		return {
			bytes: cipherBytes,
			protected: false,
			format: 'RAW',
			version,
			profile
		};
	}
	const restored = new Uint8Array(originalSize);
	const movedHeadStart = HEADER_LENGTH + tailLength;
	restored.set(cipherBytes.subarray(movedHeadStart, movedHeadStart + headShiftBytes), 0);
	restored.set(cipherBytes.subarray(HEADER_LENGTH, HEADER_LENGTH + tailLength), headShiftBytes);
	return {
		bytes: restored,
		protected: true,
		format: 'BXO1',
		version,
		profile
	};
}

function inferContentType(contentType, fileName = '', kind = '') {
	const normalizedType = String(contentType || '').trim().toLowerCase();
	if (normalizedType && normalizedType !== 'application/octet-stream') {
		return normalizedType;
	}
	const ext = getFileExtension(fileName);
	if (EXT_CONTENT_TYPE_MAP[ext]) {
		return EXT_CONTENT_TYPE_MAP[ext];
	}
	const normalizedKind = String(kind || '').trim().toLowerCase();
	if (normalizedKind === 'cover') {
		return 'image/jpeg';
	}
	if (normalizedKind === 'video') {
		return 'video/mp4';
	}
	if (normalizedKind === 'audio') {
		return 'audio/mpeg';
	}
	return 'application/octet-stream';
}

function bytesMatchAscii(bytes, offset, text) {
	if (!bytes || !text || offset < 0 || offset + text.length > bytes.length) {
		return false;
	}
	for (let i = 0; i < text.length; i += 1) {
		if (bytes[offset + i] !== text.charCodeAt(i)) {
			return false;
		}
	}
	return true;
}

function detectAudioContentTypeFromBytes(inputBytes) {
	const bytes = toUint8Array(inputBytes);
	if (!bytes.length) {
		return '';
	}
	if (bytes.length >= 12
		&& bytesMatchAscii(bytes, 0, 'RIFF')
		&& bytesMatchAscii(bytes, 8, 'WAVE')) {
		return 'audio/wav';
	}
	if (bytes.length >= 6 && bytesMatchAscii(bytes, 0, '#!AMR\n')) {
		return 'audio/amr';
	}
	if (bytes.length >= 9 && bytesMatchAscii(bytes, 0, '#!AMR-WB\n')) {
		return 'audio/amr';
	}
	if (bytes.length >= 3 && bytesMatchAscii(bytes, 0, 'ID3')) {
		return 'audio/mpeg';
	}
	if (bytes.length >= 4 && bytesMatchAscii(bytes, 0, 'OggS')) {
		return 'audio/ogg';
	}
	if (bytes.length >= 12 && bytesMatchAscii(bytes, 4, 'ftyp')) {
		const majorBrand = String.fromCharCode(bytes[8] || 0, bytes[9] || 0, bytes[10] || 0, bytes[11] || 0).toLowerCase();
		if (majorBrand.startsWith('3gp') || majorBrand.startsWith('3g2')) {
			return 'audio/3gpp';
		}
		return 'audio/mp4';
	}
	if (bytes.length >= 2 && bytes[0] === 0xFF) {
		if ((bytes[1] & 0xF6) === 0xF0) {
			return 'audio/aac';
		}
		if ((bytes[1] & 0xE0) === 0xE0) {
			return 'audio/mpeg';
		}
	}
	return '';
}

function detectImageContentTypeFromBytes(inputBytes) {
	const bytes = toUint8Array(inputBytes);
	if (!bytes.length) {
		return '';
	}
	if (bytes.length >= 3
		&& bytes[0] === 0xFF
		&& bytes[1] === 0xD8
		&& bytes[2] === 0xFF) {
		return 'image/jpeg';
	}
	if (bytes.length >= 8
		&& bytes[0] === 0x89
		&& bytes[1] === 0x50
		&& bytes[2] === 0x4E
		&& bytes[3] === 0x47
		&& bytes[4] === 0x0D
		&& bytes[5] === 0x0A
		&& bytes[6] === 0x1A
		&& bytes[7] === 0x0A) {
		return 'image/png';
	}
	if (bytes.length >= 12
		&& bytesMatchAscii(bytes, 0, 'RIFF')
		&& bytesMatchAscii(bytes, 8, 'WEBP')) {
		return 'image/webp';
	}
	if (bytes.length >= 6
		&& (bytesMatchAscii(bytes, 0, 'GIF87a') || bytesMatchAscii(bytes, 0, 'GIF89a'))) {
		return 'image/gif';
	}
	if (bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4D) {
		return 'image/bmp';
	}
	if (bytes.length >= 5) {
		const headText = String.fromCharCode.apply(null, Array.from(bytes.slice(0, Math.min(bytes.length, 128)))).trimStart().toLowerCase();
		if (headText.startsWith('<svg') || headText.startsWith('<?xml')) {
			return 'image/svg+xml';
		}
	}
	return '';
}

function detectContentTypeFromBytes(inputBytes, kind = '', fallbackContentType = '') {
	const normalizedKind = String(kind || '').trim().toLowerCase();
	if (normalizedKind === 'audio') {
		return detectAudioContentTypeFromBytes(inputBytes)
			|| inferContentType(fallbackContentType, '', normalizedKind);
	}
	if (normalizedKind === 'avatar' || normalizedKind === 'image' || normalizedKind === 'cover') {
		return detectImageContentTypeFromBytes(inputBytes)
			|| inferContentType(fallbackContentType, '', normalizedKind);
	}
	return inferContentType(fallbackContentType, '', normalizedKind);
}

function createObjectUrlFromBytes(bytes, contentType = 'application/octet-stream') {
	const blob = new Blob([toUint8Array(bytes)], {
		type: contentType || 'application/octet-stream'
	});
	return {
		blob,
		url: window.URL.createObjectURL(blob)
	};
}

function revokeObjectUrl(url) {
	if (typeof url === 'string' && url.startsWith('blob:')) {
		window.URL.revokeObjectURL(url);
	}
}

export {
	PROFILE,
	isClientDecryptSupported,
	protectAssetBytes,
	restoreProtectedAssetBytes,
	detectContentTypeFromBytes,
	inferContentType,
	createProtectedBlobFromFile,
	createObjectUrlFromBytes,
	revokeObjectUrl
};
